if (!customElements.get('product-family-selector')) {
  const navigationStorageKey = 'strix:product-family-selector:navigation';
  const navigationStorageLifetime = 5 * 60 * 1000;

  class ProductFamilySelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.dataElement = this.querySelector('[data-product-family-selector-data]');
      this.contentElement = this.querySelector('[data-product-family-selector-content]');
      this.dimensionsElement = this.querySelector('[data-product-family-selector-dimensions]');
      this.statusElement = this.querySelector('[data-product-family-selector-status]');
      this.statusTextElement = this.querySelector('[data-product-family-selector-status-text]');
      this.errorElement = this.querySelector('[data-product-family-selector-error]');
      this.triggerElement = this.querySelector('[data-product-family-selector-trigger]');
      this.expandedElement = this.querySelector('[data-product-family-selector-expanded]');
      this.collapseElement = this.querySelector('[data-product-family-selector-collapse]');

      try {
        const parsedData = JSON.parse(this.dataElement?.textContent || 'null');
        this.model = this.validateData(parsedData);
        this.selection = [...this.model.currentProduct.values];
        this.priorities = Array(this.model.dimensionCount).fill(0);
        this.resetDimensions = new Set();
        this.interactionPriority = 0;
        this.tracking = window.ProductFamilySelectorTracking
          ? new window.ProductFamilySelectorTracking(this, this.model)
          : null;
        this.addEventListener('click', this.handleClick.bind(this), { signal: this.abortController.signal });
        this.addEventListener('pointerover', this.handlePrefetchIntent.bind(this), {
          signal: this.abortController.signal,
        });
        this.addEventListener('focusin', this.handlePrefetchIntent.bind(this), {
          signal: this.abortController.signal,
        });
        this.addEventListener('touchstart', this.handlePrefetchIntent.bind(this), {
          passive: true,
          signal: this.abortController.signal,
        });

        const shouldRestoreExpandedState = this.consumeNavigationState();
        if (this.model.layoutMode === 'inline') {
          if (!this.ensureDimensionsRendered()) return;
        } else if (shouldRestoreExpandedState) {
          if (!this.expandSelector({ moveFocus: false })) return;
        } else {
          this.scheduleDimensionsRender();
        }

        this.setAttribute('data-ready', '');
        this.removeAttribute('hidden');
        this.tracking?.trackView();
      } catch (error) {
        this.showDataError(error);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.cancelScheduledDimensionsRender();
      this.initialized = false;
    }

    normalizeValue(value) {
      const normalizeScalar = (scalar) => {
        if (typeof scalar === 'string') {
          const label = scalar.trim();
          if (!label) throw new Error('Missing product value');
          return { key: `string:${label}`, label };
        }

        if (typeof scalar === 'number' && Number.isFinite(scalar)) {
          return { key: `number:${scalar}`, label: String(scalar) };
        }

        if (typeof scalar === 'boolean') {
          return { key: `boolean:${scalar}`, label: String(scalar) };
        }

        throw new Error('Invalid product value');
      };

      if (!Array.isArray(value)) return normalizeScalar(value);
      if (value.length === 0) throw new Error('Missing product value');

      const items = value.map(normalizeScalar);
      return {
        key: `list:${JSON.stringify(items.map((item) => item.key))}`,
        label: items.map((item) => item.label).join(', '),
      };
    }

    validateData(data) {
      if (!data || typeof data !== 'object') throw new Error('Missing selector data');
      if (data.schemaVersion !== 2) throw new Error('Invalid data version');
      const familyId = String(data.familyId ?? '').trim();
      if (!familyId) throw new Error('Invalid family');
      if (!Array.isArray(data.dimensions) || data.dimensions.length < 1 || data.dimensions.length > 10) {
        throw new Error('Invalid dimensions');
      }
      if (!Array.isArray(data.products) || data.products.length < 2 || data.products.length > 128) {
        throw new Error('Invalid products');
      }

      const locale = typeof data.locale === 'string' && data.locale.trim() ? data.locale.trim() : 'en';
      const presentation = data.presentation || {};
      const layoutMode = presentation.layoutMode === 'inline' ? 'inline' : 'compact';
      const desktopDisplayMode = presentation.desktopDisplayMode === 'auto' ? 'auto' : 'pills';
      const parsedDropdownThreshold = Number(presentation.desktopDropdownThreshold);
      const desktopDropdownThreshold = Number.isInteger(parsedDropdownThreshold) &&
        parsedDropdownThreshold >= 2 && parsedDropdownThreshold <= 20
        ? parsedDropdownThreshold
        : 7;
      let collator;
      try {
        collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' });
      } catch (error) {
        collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
      }

      const dimensionCount = data.dimensions.length;
      const dimensions = data.dimensions.map((dimension) => {
        if (!dimension || typeof dimension.label !== 'string' || !dimension.label.trim()) {
          throw new Error('Invalid dimension label');
        }
        if (
          typeof dimension.metafield !== 'string' ||
          !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(dimension.metafield.trim())
        ) {
          throw new Error('Invalid dimension metafield');
        }

        const rawOrder = dimension.order == null ? [] : dimension.order;
        if (!Array.isArray(rawOrder)) throw new Error('Invalid option order');
        const order = [];
        rawOrder.forEach((value) => {
          if (typeof value !== 'string' || !value.trim()) return;
          const label = value.trim();
          if (!order.some((orderedLabel) => collator.compare(orderedLabel, label) === 0)) order.push(label);
        });

        return {
          label: dimension.label.trim(),
          metafield: dimension.metafield.trim(),
          order,
        };
      });

      const ids = new Set();
      const urls = new Set();
      const combinations = new Set();
      const optionMaps = dimensions.map(() => new Map());
      const normalizedProducts = data.products.map((product) => {
        if (!product || typeof product !== 'object') throw new Error('Invalid product');

        const id = String(product.id ?? '').trim();
        const url = typeof product.url === 'string' ? product.url.trim() : '';
        if (!id || !url || /^(?:data|javascript):/i.test(url)) throw new Error('Invalid product');
        if (ids.has(id) || urls.has(url)) throw new Error('Duplicate product');
        if (!Array.isArray(product.values) || product.values.length !== dimensionCount) {
          throw new Error('Invalid product values');
        }

        const values = product.values.map((value, dimensionIndex) => {
          const normalizedValue = this.normalizeValue(value);
          const existingOption = optionMaps[dimensionIndex].get(normalizedValue.key);
          if (existingOption && existingOption.label !== normalizedValue.label) {
            throw new Error('Ambiguous product value');
          }
          optionMaps[dimensionIndex].set(normalizedValue.key, normalizedValue);
          return normalizedValue;
        });

        const combination = JSON.stringify(values.map((value) => value.key));
        if (combinations.has(combination)) throw new Error('Duplicate product combination');

        ids.add(id);
        urls.add(url);
        combinations.add(combination);
        return { id, url, values };
      });

      const optionIndexes = [];
      const options = optionMaps.map((optionMap, dimensionIndex) => {
        const availableOptions = [...optionMap.values()];
        availableOptions.forEach((option, optionIndex) => {
          if (
            availableOptions.some(
              (otherOption, otherIndex) =>
                otherIndex !== optionIndex && collator.compare(otherOption.label, option.label) === 0
            )
          ) {
            throw new Error('Ambiguous option value');
          }
        });

        const orderedOptions = [];
        const usedOptionKeys = new Set();
        dimensions[dimensionIndex].order.forEach((orderedLabel) => {
          const matchingOption = availableOptions.find(
            (option) => !usedOptionKeys.has(option.key) && collator.compare(option.label, orderedLabel) === 0
          );
          if (matchingOption) {
            orderedOptions.push(matchingOption);
            usedOptionKeys.add(matchingOption.key);
          }
        });

        const remainingOptions = availableOptions
          .filter((option) => !usedOptionKeys.has(option.key))
          .sort(
            (first, second) =>
              collator.compare(first.label, second.label) || first.label.localeCompare(second.label)
          );
        orderedOptions.push(...remainingOptions);

        optionIndexes[dimensionIndex] = new Map(
          orderedOptions.map((option, optionIndex) => [option.key, optionIndex])
        );
        return orderedOptions.map((option) => option.label);
      });

      const products = normalizedProducts.map((product) => ({
        id: product.id,
        url: product.url,
        values: product.values.map((value, dimensionIndex) => optionIndexes[dimensionIndex].get(value.key)),
      }));

      const currentProductId = String(data.currentProductId || '').trim();
      const currentProduct = products.find((product) => product.id === currentProductId);
      if (!currentProduct) throw new Error('Current product missing');

      const messages = data.messages || {};
      [
        'adjustedSingle',
        'adjustedMultiple',
        'resetBadge',
        'adjustsSelection',
        'chooseValue',
        'change',
        'dataError',
      ].forEach((messageKey) => {
        if (typeof messages[messageKey] !== 'string' || !messages[messageKey]) {
          throw new Error('Missing message');
        }
      });

      return {
        familyId,
        dimensionCount,
        dimensions,
        options,
        products,
        currentProduct,
        currentProductId,
        locale,
        layoutMode,
        desktopDisplayMode,
        desktopDropdownThreshold,
        messages,
      };
    }

    expandSelector({ moveFocus = true } = {}) {
      if (this.model.layoutMode !== 'compact' || !this.triggerElement || !this.expandedElement) return false;

      if (!this.ensureDimensionsRendered()) return false;
      this.triggerElement.setAttribute('aria-expanded', 'true');
      this.expandedElement.hidden = false;
      this.triggerElement.hidden = true;

      if (moveFocus) window.requestAnimationFrame(() => this.focusExpandedSelector());
      return true;
    }

    collapseSelector() {
      if (
        this.model.layoutMode !== 'compact' ||
        !this.triggerElement ||
        !this.expandedElement ||
        !this.collapseElement
      ) return;

      this.selection = [...this.model.currentProduct.values];
      this.priorities = Array(this.model.dimensionCount).fill(0);
      this.resetDimensions = new Set();
      this.interactionPriority = 0;
      this.clearNavigationState();
      this.hideAdjustedStatus();
      this.render();

      this.triggerElement.setAttribute('aria-expanded', 'false');
      this.expandedElement.hidden = true;
      this.triggerElement.hidden = false;
      window.requestAnimationFrame(() => this.triggerElement.focus());
    }

    focusExpandedSelector() {
      const firstFieldset = this.dimensionsElement?.children[0];
      if (!firstFieldset) return;

      const desktopDropdown = window.matchMedia('(min-width: 750px)').matches &&
        firstFieldset.querySelector('.product-family-selector__dropdown');
      const nextFocus = desktopDropdown
        ? firstFieldset.querySelector('.product-family-selector__summary')
        : firstFieldset.querySelector(
          'button[aria-pressed="true"], button[data-family-dimension], a[href]'
        );
      nextFocus?.focus();
    }

    consumeNavigationState() {
      if (this.model.layoutMode !== 'compact' || window.Shopify?.designMode) return false;

      try {
        const storedState = window.sessionStorage.getItem(navigationStorageKey);
        if (!storedState) return false;

        window.sessionStorage.removeItem(navigationStorageKey);
        const state = JSON.parse(storedState);
        const expiresAt = Number(state?.expiresAt);
        return (
          state?.familyId === this.model.familyId &&
          state?.targetProductId === this.model.currentProductId &&
          Number.isFinite(expiresAt) &&
          expiresAt >= Date.now()
        );
      } catch (error) {
        return false;
      }
    }

    rememberNavigationState(targetProductId) {
      if (this.model.layoutMode !== 'compact' || window.Shopify?.designMode) return;

      try {
        window.sessionStorage.setItem(
          navigationStorageKey,
          JSON.stringify({
            familyId: this.model.familyId,
            targetProductId,
            expiresAt: Date.now() + navigationStorageLifetime,
          })
        );
      } catch (error) {
        // Navigation remains functional when browser storage is unavailable.
      }
    }

    clearNavigationState() {
      try {
        window.sessionStorage.removeItem(navigationStorageKey);
      } catch (error) {
        // No action needed when browser storage is unavailable.
      }
    }

    getMatches(selection) {
      return this.model.products.filter((product) =>
        selection.every((selectedOption, dimensionIndex) =>
          selectedOption === null || product.values[dimensionIndex] === selectedOption
        )
      );
    }

    resolveSelection(changedDimension, optionIndex, commitPriority = false) {
      const selection = Array(this.model.dimensionCount).fill(null);
      const priorities = [...this.priorities];
      const nextPriority = this.interactionPriority + 1;
      priorities[changedDimension] = nextPriority;
      selection[changedDimension] = optionIndex;

      let candidates = this.model.products.filter(
        (product) => product.values[changedDimension] === optionIndex
      );

      const remainingDimensions = this.selection
        .map((selectedOption, dimensionIndex) => ({
          dimensionIndex,
          selectedOption,
          priority: priorities[dimensionIndex],
        }))
        .filter(
          (entry) => entry.dimensionIndex !== changedDimension && entry.selectedOption !== null
        )
        .sort((first, second) =>
          second.priority - first.priority || first.dimensionIndex - second.dimensionIndex
        );

      const removedOptions = [];
      remainingDimensions.forEach((entry) => {
        const matchingCandidates = candidates.filter(
          (product) => product.values[entry.dimensionIndex] === entry.selectedOption
        );
        if (matchingCandidates.length > 0) {
          selection[entry.dimensionIndex] = entry.selectedOption;
          candidates = matchingCandidates;
        } else {
          priorities[entry.dimensionIndex] = 0;
          removedOptions.push({
            dimensionIndex: entry.dimensionIndex,
            label: this.model.options[entry.dimensionIndex][entry.selectedOption],
            dimensionLabel: this.model.dimensions[entry.dimensionIndex].label,
          });
        }
      });

      if (commitPriority) this.interactionPriority = nextPriority;

      return { selection, priorities, candidates, removedOptions };
    }

    isCompatible(dimensionIndex, optionIndex) {
      const nextSelection = [...this.selection];
      nextSelection[dimensionIndex] = optionIndex;
      return this.getMatches(nextSelection).length > 0;
    }

    optionControl(dimensionIndex, optionIndex) {
      const isActive = this.selection[dimensionIndex] === optionIndex;
      const isCompatible = this.isCompatible(dimensionIndex, optionIndex);
      const resolved = this.resolveSelection(dimensionIndex, optionIndex);
      const uniqueProduct = resolved.candidates.length === 1 ? resolved.candidates[0] : null;
      const shouldLink = !isActive && uniqueProduct && uniqueProduct.id !== this.model.currentProductId;
      const control = document.createElement(shouldLink ? 'a' : 'button');
      const optionLabel = this.model.options[dimensionIndex][optionIndex];

      control.className = 'product-family-selector__option';
      if (!isCompatible) control.classList.add('product-family-selector__option--conflict');
      control.textContent = optionLabel;

      if (shouldLink) {
        control.href = uniqueProduct.url;
        control.dataset.familyProductId = uniqueProduct.id;
      } else {
        control.type = 'button';
        control.setAttribute('aria-pressed', String(isActive));
      }
      control.dataset.familyDimension = String(dimensionIndex);
      control.dataset.familyOption = String(optionIndex);

      if (!isCompatible && !isActive) {
        control.setAttribute('aria-label', `${optionLabel}. ${this.model.messages.adjustsSelection}`);
      }

      return control;
    }

    dimensionFieldset(dimensionIndex) {
      const fieldset = document.createElement('fieldset');
      const legend = document.createElement('legend');
      const pills = document.createElement('div');
      const options = this.model.options[dimensionIndex];
      const useDesktopDropdown = this.model.desktopDisplayMode === 'auto' &&
        options.length >= this.model.desktopDropdownThreshold;

      fieldset.className = 'product-family-selector__dimension';
      legend.className = 'product-family-selector__legend';
      legend.textContent = this.model.dimensions[dimensionIndex].label;
      if (this.resetDimensions.has(dimensionIndex)) {
        fieldset.classList.add('product-family-selector__dimension--reset');
        const resetBadge = document.createElement('span');
        resetBadge.className = 'product-family-selector__reset-badge';
        resetBadge.textContent = this.model.messages.resetBadge;
        legend.append(resetBadge);
      }
      pills.className = 'product-family-selector__pills';
      pills.setAttribute('role', 'group');
      pills.setAttribute('aria-label', this.model.dimensions[dimensionIndex].label);

      if (useDesktopDropdown) pills.classList.add('product-family-selector__pills--desktop-dropdown');
      options.forEach((option, optionIndex) => {
        pills.append(this.optionControl(dimensionIndex, optionIndex));
      });

      fieldset.append(legend, pills);
      if (useDesktopDropdown) fieldset.append(this.dimensionDropdown(dimensionIndex));
      return fieldset;
    }

    dimensionDropdown(dimensionIndex) {
      const details = document.createElement('details');
      const summary = document.createElement('summary');
      const list = document.createElement('div');
      const selectedOption = this.selection[dimensionIndex];
      const dimensionLabel = this.model.dimensions[dimensionIndex].label;
      const chooseValueLabel = this.model.messages.chooseValue.replace('__DIMENSION__', dimensionLabel);

      details.className = 'product-family-selector__dropdown';
      summary.className = 'product-family-selector__summary';
      summary.textContent = selectedOption === null
        ? chooseValueLabel
        : this.model.options[dimensionIndex][selectedOption];
      summary.setAttribute('aria-label', chooseValueLabel);
      list.className = 'product-family-selector__dropdown-list';

      this.model.options[dimensionIndex].forEach((option, optionIndex) => {
        list.append(this.optionControl(dimensionIndex, optionIndex));
      });

      details.append(summary, list);
      return details;
    }

    handlePrefetchIntent(event) {
      const productLink = event.target.closest?.('a[data-family-product-id]');
      if (!productLink || !this.contains(productLink)) return;

      try {
        const url = new URL(productLink.href, window.location.href);
        if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) return;

        window.prefetched = window.prefetched || new Set();
        if (window.prefetched.has(url.href)) return;

        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url.href;
        link.as = 'document';
        link.dataset.prefetched = '';
        document.head.append(link);
        window.prefetched.add(url.href);
      } catch (error) {
        // Invalid or unavailable URLs must not affect selector interaction.
      }
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-product-family-selector-trigger]');
      if (trigger && this.contains(trigger)) {
        if (this.expandSelector()) this.tracking?.trackOpen();
        return;
      }

      const collapseControl = event.target.closest('[data-product-family-selector-collapse]');
      if (collapseControl && this.contains(collapseControl)) {
        this.collapseSelector();
        return;
      }

      const productLink = event.target.closest('a[data-family-product-id]');
      if (productLink && this.contains(productLink)) {
        const dimensionIndex = Number(productLink.dataset.familyDimension);
        const optionIndex = Number(productLink.dataset.familyOption);
        const resolved = this.resolveSelection(dimensionIndex, optionIndex);
        const trackingParams = this.tracking?.trackSelection({
          dimensionIndex,
          optionIndex,
          previousOptionIndex: this.selection[dimensionIndex],
          removedOptionCount: resolved.removedOptions.length,
          targetProductId: productLink.dataset.familyProductId,
          controlType: productLink.closest('.product-family-selector__dropdown') ? 'dropdown' : 'pill',
        });
        if (trackingParams) this.tracking.trackNavigate(trackingParams);

        const usesCurrentTab =
          event.button === 0 &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.shiftKey &&
          !event.altKey &&
          (!productLink.target || productLink.target === '_self');
        if (usesCurrentTab) this.rememberNavigationState(productLink.dataset.familyProductId);
        return;
      }

      const control = event.target.closest('button[data-family-dimension][data-family-option]');
      if (!control || !this.contains(control)) return;

      this.handleOptionControl(control);
    }

    handleOptionControl(control) {
      const dimensionIndex = Number(control.dataset.familyDimension);
      const optionIndex = Number(control.dataset.familyOption);
      if (this.selection[dimensionIndex] === optionIndex) return;

      this.hideAdjustedStatus();
      const previousOptionIndex = this.selection[dimensionIndex];
      const resolved = this.resolveSelection(dimensionIndex, optionIndex, true);
      this.tracking?.trackSelection({
        dimensionIndex,
        optionIndex,
        previousOptionIndex,
        removedOptionCount: resolved.removedOptions.length,
        targetProductId: resolved.candidates.length === 1 ? resolved.candidates[0].id : undefined,
        controlType: control.closest('.product-family-selector__dropdown') ? 'dropdown' : 'pill',
      });
      const resetDimensions = new Set(this.resetDimensions);
      resetDimensions.delete(dimensionIndex);
      resolved.removedOptions.forEach((option) => resetDimensions.add(option.dimensionIndex));
      this.selection = resolved.selection;
      this.priorities = resolved.priorities;
      this.resetDimensions = resetDimensions;
      this.render();
      this.focusRenderedControl(dimensionIndex, optionIndex);

      if (resolved.removedOptions.length > 0) {
        this.showAdjustedStatus(
          resolved.removedOptions.map((option) => `${option.label} (${option.dimensionLabel})`),
          this.model.options[dimensionIndex][optionIndex],
          this.model.dimensions[dimensionIndex].label
        );
      }
    }

    focusRenderedControl(dimensionIndex, optionIndex) {
      const fieldset = this.dimensionsElement.children[dimensionIndex];
      if (!fieldset) return;

      const desktopDropdown = window.matchMedia('(min-width: 750px)').matches &&
        fieldset.querySelector('.product-family-selector__dropdown');
      const nextFocus = desktopDropdown
        ? fieldset.querySelector('.product-family-selector__summary')
        : fieldset.querySelector(
          `button[data-family-dimension="${dimensionIndex}"][data-family-option="${optionIndex}"]`
        );
      nextFocus?.focus();
    }

    render() {
      const fragment = document.createDocumentFragment();
      this.model.dimensions.forEach((dimension, dimensionIndex) => {
        fragment.append(this.dimensionFieldset(dimensionIndex));
      });
      this.dimensionsElement.replaceChildren(fragment);
      this.dimensionsRendered = true;
    }

    ensureDimensionsRendered() {
      this.cancelScheduledDimensionsRender();
      if (this.dimensionsRendered) return true;

      try {
        this.render();
        return true;
      } catch (error) {
        this.showDataError(error);
        return false;
      }
    }

    scheduleDimensionsRender() {
      if (this.dimensionsRendered || this.scheduledRenderId != null) return;

      const renderWhenIdle = () => {
        this.scheduledRenderId = null;
        this.scheduledRenderType = null;
        if (!this.isConnected || this.dimensionsRendered) return;

        this.ensureDimensionsRendered();
      };

      if (typeof window.requestIdleCallback === 'function') {
        this.scheduledRenderType = 'idle';
        this.scheduledRenderId = window.requestIdleCallback(renderWhenIdle, { timeout: 1500 });
      } else {
        this.scheduledRenderType = 'timeout';
        this.scheduledRenderId = window.setTimeout(renderWhenIdle, 500);
      }
    }

    cancelScheduledDimensionsRender() {
      if (this.scheduledRenderId == null) return;

      if (this.scheduledRenderType === 'idle' && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(this.scheduledRenderId);
      } else {
        window.clearTimeout(this.scheduledRenderId);
      }

      this.scheduledRenderId = null;
      this.scheduledRenderType = null;
    }

    showAdjustedStatus(removedLabels, selectedLabel, selectedDimensionLabel) {
      let removedText;
      try {
        removedText = new Intl.ListFormat(this.model.locale, { style: 'long', type: 'conjunction' }).format(removedLabels);
      } catch (error) {
        removedText = removedLabels.join(', ');
      }

      const template = removedLabels.length === 1
        ? this.model.messages.adjustedSingle
        : this.model.messages.adjustedMultiple;
      const selectedText = template.includes('__SELECTED_DIMENSION__')
        ? selectedLabel
        : `${selectedLabel} (${selectedDimensionLabel})`;
      this.statusTextElement.textContent = template
        .replaceAll('__REMOVED__', removedText)
        .replaceAll('__SELECTED__', selectedText)
        .replaceAll('__SELECTED_DIMENSION__', selectedDimensionLabel);
      this.statusElement.hidden = false;
    }

    hideAdjustedStatus() {
      if (this.statusTextElement) this.statusTextElement.textContent = '';
      if (this.statusElement) this.statusElement.hidden = true;
    }

    showDataError(error) {
      this.cancelScheduledDimensionsRender();
      this.removeAttribute('data-ready');
      this.setAttribute('hidden', '');
      if (!window.Shopify?.designMode) return;
      this.errorElement.hidden = false;
      this.errorElement.textContent = this.model?.messages?.dataError || this.errorElement.textContent;
      this.setAttribute('data-error-visible', '');
      this.removeAttribute('hidden');
      console.warn('Product family selector:', error);
    }
  }

  customElements.define('product-family-selector', ProductFamilySelector);
}
